import type { AttendanceData } from "@/artifacts/admin/pages/Dashboard";
import { buildAdminAttendanceRows } from "@/artifacts/admin/utils/dashboard/buildAttendanceRows";
import { HStack } from "@/components/layout/HStack";
import { Button } from "@/components/ui/Button/Button";
import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { formatNumber } from "@/utils/formatNumber";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
} from "@tanstack/react-table";
import { format } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Eye,
  EyeClosed,
  Filter,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { AdminAttendanceRow } from "@/types/tables";

export const getColumns = (
  absentStudents: {
    rotationId: string;
    rotationDate: string;
    sessionId: string;
    sessionName: string;
    students: {
      _id: string;
      name: string;
      gradYear: number;
    }[];
  } | null,
  setAbsentSelection: (
    next: { rotationId: string; sessionId: string } | null,
  ) => void,
): ColumnDef<AdminAttendanceRow>[] => [
  {
    accessorKey: "rotationDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Date{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => format(row.original.rotationDate, "MMMM dd, yyyy"),
    sortingFn: "datetime",
    filterFn: "arrIncludesSome",
  },
  {
    accessorKey: "sessionName",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Session{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    filterFn: "arrIncludesSome",
  },
  {
    accessorKey: "records",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Records{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => formatNumber(row.original.records),
  },
  {
    accessorKey: "present",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Present{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => formatNumber(row.original.present),
  },
  {
    accessorKey: "absent",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Absent{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => formatNumber(row.original.absent),
  },
  {
    accessorKey: "rate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Rate{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ getValue }) => `${getValue()}%`,
  },
  {
    id: "actions",
    header: () => (
      <div style={{ textAlign: "right", fontWeight: "var(--font-medium)" }}>
        Actions
      </div>
    ),
    cell: ({ row }) => {
      const isSelected =
        row.original.rotationId === absentStudents?.rotationId &&
        row.original.sessionId === absentStudents.sessionId;

      return (
        <HStack justify="flex-end">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isSelected ? "outline" : "default"}
                size="icon-small"
                onClick={() =>
                  setAbsentSelection(
                    isSelected
                      ? null
                      : {
                          rotationId: row.original.rotationId,
                          sessionId: row.original.sessionId,
                        },
                  )
                }
              >
                {isSelected ? <EyeClosed /> : <Eye />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isSelected ? "Hide" : "Show"} absent students for{" "}
              {row.original.sessionName} on{" "}
              {format(row.original.rotationDate, "MMMM dd, yyyy")}
            </TooltipContent>
          </Tooltip>
        </HStack>
      );
    },
  },
];

const DataTable = ({
  data,
  columns,
}: {
  data: AdminAttendanceRow[];
  columns: ColumnDef<AdminAttendanceRow>[];
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  // Rotation Filtering

  const rotCol = table.getColumn("rotationDate");
  const selectedRot = (rotCol?.getFilterValue() as string[] | undefined) ?? [];

  const rotations = useMemo(() => {
    const set = new Set<string>();
    for (const d of data) if (d.rotationDate) set.add(d.rotationDate);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const toggleRot = (rot: string) => {
    const next = selectedRot.includes(rot)
      ? selectedRot.filter((s) => s !== rot)
      : [...selectedRot, rot];

    rotCol?.setFilterValue(next);
  };

  const clearRotFilter = () => {
    rotCol?.setFilterValue([]);
  };

  // Session Filtering

  const seshCol = table.getColumn("sessionName");
  const selectedSesh =
    (seshCol?.getFilterValue() as string[] | undefined) ?? [];

  const sessions = useMemo(() => {
    const set = new Set<string>();
    for (const d of data) if (d.sessionName) set.add(d.sessionName);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const toggleSesh = (sesh: string) => {
    const next = selectedSesh.includes(sesh)
      ? selectedSesh.filter((s) => s !== sesh)
      : [...selectedSesh, sesh];

    seshCol?.setFilterValue(next);
  };

  const clearSeshFilter = () => {
    seshCol?.setFilterValue([]);
  };

  const totalCount = table.getCoreRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;

  return (
    <>
      <HStack align="center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              style={{
                height: "fit-content",
                padding: "var(--space-xxs) var(--space-xs)",
              }}
            >
              <Filter /> Filter
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Dates</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {rotations.map((r) => (
                  <DropdownMenuCheckboxItem
                    key={r}
                    checked={selectedRot.includes(r)}
                    onCheckedChange={() => toggleRot(r)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {format(r, "MMMM dd, yyyy")}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    clearRotFilter();
                  }}
                  disabled={selectedRot.length === 0}
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Sessions</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {sessions.map((s) => (
                  <DropdownMenuCheckboxItem
                    key={s}
                    checked={selectedSesh.includes(s)}
                    onCheckedChange={() => toggleSesh(s)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {s}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    clearSeshFilter();
                  }}
                  disabled={selectedSesh.length === 0}
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuContent>
        </DropdownMenu>
        <p
          style={{
            color: "var(--muted-foreground)",
          }}
        >
          {filteredCount} of {totalCount} sessions
        </p>
      </HStack>
      <Table borderColor="var(--secondary-border)">
        <TableHeader>
          {table.getHeaderGroups().map((h) => (
            <TableRow key={h.id}>
              {h.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>No records</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

const AttendanceTable = ({
  data,
  absentStudents,
  setAbsentSelection,
}: {
  data: AttendanceData;
  absentStudents: {
    rotationId: string;
    rotationDate: string;
    sessionId: string;
    sessionName: string;
    students: {
      _id: string;
      name: string;
      gradYear: number;
    }[];
  } | null;
  setAbsentSelection: (
    next: { rotationId: string; sessionId: string } | null,
  ) => void;
}) => {
  const rows = useMemo(() => buildAdminAttendanceRows(data.rotations), [data]);
  const columns = useMemo(
    () => getColumns(absentStudents, setAbsentSelection),
    [absentStudents, setAbsentSelection],
  );

  return (
    <DashboardCard>
      <DataTable data={rows} columns={columns} />
    </DashboardCard>
  );
};

export { AttendanceTable };
